import { LoadingDrawer, CustomButton } from '@/shared/ui/components';
import { closeIconV2 } from '@/shared/ui/assets';
import { Drawer } from 'antd';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useBiodirectInfoDrawer } from './useBiodirectInfoDrawer';
import type { BiodirectInfoDrawerProps } from './biodirectInfoDrawer.types';
import './biodirectInfoDrawer.scss';
import DrawerContent from './drawerContent/drawerContent';

/**
 * Side drawer showing the Biodirect shipping status of a study for both recipient and sender information. Pulls its state and handlers from useBiodirectInfoDrawer, keeps the content behind a loading state until the biofluxDirect data arrives, shadows the title while the body scrolls, and clears the loaded data and calls onClose once the drawer has finished closing.
 *
 * @param study - Study whose Biodirect shipment is displayed.
 * @param onClose - Callback fired after the drawer closes.
 * @returns The Biodirect status drawer markup.
 */
const BiodirectInfoDrawer = ({ study, onClose }: BiodirectInfoDrawerProps) => {
  const { state, setState, handleRefreshTracking, handleCloseButtonClick, handleScroll } =
    useBiodirectInfoDrawer({ study, onClose });

  const closeButton = (
    <CustomButton
      id="close-btn"
      style="neutral"
      type="tertiary"
      size="small"
      icon={<ReactSVG src={closeIconV2} />}
      onClick={handleCloseButtonClick}
      ariaLabel={t`Close`}
    />
  );

  return (
    <Drawer
      id="biodirect-info-drawer-id"
      aria-label={t`Biodirect status`}
      rootClassName="custom-drawer biodirect-info-drawer"
      width={500}
      closable={false}
      onClose={handleCloseButtonClick}
      open={state.openDrawer}
      afterOpenChange={(open) => {
        if (!open) {
          setState({ biofluxDirect: null });
          onClose();
        }
      }}
    >
      <LoadingDrawer isLoading={state.isLoading} rightIcon={closeButton}>
        <div
          className={classNames('drawer-title-container', {
            '--has-box-shadow': state.isScroll,
          })}
        >
          <span className="drawer-title">{t`Biodirect status`}</span>
          {closeButton}
        </div>
        <div className="drawer-body-container" onScroll={handleScroll as any}>
          <div className="drawer-body">
            <DrawerContent
              study={study}
              biofluxDirect={state.biofluxDirect}
              isRecipientInformation
              isDrawer
              handleRefreshTracking={handleRefreshTracking}
            />
          </div>
          <div className="drawer-body">
            <DrawerContent
              study={study}
              biofluxDirect={state.biofluxDirect}
              isDrawer
              handleRefreshTracking={handleRefreshTracking}
            />
          </div>
        </div>
      </LoadingDrawer>
    </Drawer>
  );
};

export default BiodirectInfoDrawer;
